/**
 * `<ActionModalButton>` — a reusable staff button that dispatches ONE canonical
 * action through the standard chokepoint (`useDispatch` → `POST
 * /api/dispatch/:action`), opening a small modal for any staff-entered fields.
 *
 * Two exports, one implementation: `<ActionModal>` is the modal alone (the host
 * owns `open`), and `<ActionModalButton>` is that modal plus a trigger that owns
 * `open` for itself. The split exists because a trigger rendered inside a
 * `<PopMenu>` cannot own the state — closing the menu unmounts it mid-open.
 *
 * Why not reuse `<DispatchForm>` (the `/status` "Run with data…" form)? That renders the
 * action's FULL JSON Schema — it can't lock a context field. These buttons live
 * on entity pages (a member, a payment row, a slot) where the ids
 * (`member_id`, `payment_id`, `booking_id`, …) come FROM the row and must NOT be
 * hand-typed. `prefill` carries those locked ids; `fields` declares only what
 * staff actually fill in. Empty `fields` → a confirm-only modal (e.g. Refund,
 * Cancel).
 *
 * It is intentionally declarative (a small `ActionField` union) rather than
 * schema-derived: it gives clean labels, enum dropdowns, £-to-minor and
 * datetime-local→ISO coercion, and locked context — the same hand-rolled style
 * as `PurchaseCreditsModal`. The single-action-layer invariant holds: every
 * dispatch goes through the same `dispatchAction()` chokepoint as every other
 * surface, and `useDispatch` busts the read caches on success.
 */

import { useId, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useBackdropDismiss } from "../lib/use-backdrop-dismiss";
import { failureText } from "../lib/field-errors";
import { useDispatch, type DispatchErrorState } from "../lib/use-dispatch";
import { zonedDatetimeInputToIso } from "../lib/studio-tz";
import {
  MultiSelectField,
  type MultiSelectGroup,
} from "./MultiSelectField";
import { SelectField } from "./SelectField";

/** One staff-entered field in the action modal. */
export type ActionField =
  | {
      readonly kind: "text";
      readonly name: string;
      readonly label: string;
      readonly required?: boolean;
      readonly placeholder?: string;
      readonly maxLength?: number;
      readonly defaultValue?: string;
    }
  | {
      readonly kind: "textarea";
      readonly name: string;
      readonly label: string;
      readonly required?: boolean;
      readonly placeholder?: string;
      readonly maxLength?: number;
      readonly defaultValue?: string;
    }
  | {
      readonly kind: "number";
      readonly name: string;
      readonly label: string;
      readonly required?: boolean;
      readonly min?: number;
      readonly max?: number;
      readonly step?: number;
      readonly placeholder?: string;
      readonly defaultValue?: string;
      /** Multiply by 100 and round → minor units (pence). */
      readonly minorUnits?: boolean;
    }
  | {
      readonly kind: "select";
      readonly name: string;
      readonly label: string;
      readonly required?: boolean;
      readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
      readonly defaultValue?: string;
      /** Coerce the chosen value to a number before dispatch (e.g. an integer rating). */
      readonly numeric?: boolean;
    }
  | {
      readonly kind: "datetime";
      readonly name: string;
      readonly label: string;
      readonly required?: boolean;
      /** A `datetime-local` value (local wall-clock); serialised to ISO on submit. */
      readonly defaultValue?: string;
    }
  | {
      /**
       * Grouped checkbox dropdown (`<MultiSelectField>`). The VALUE is a
       * JSON-encoded string array while anything is selected and the EMPTY
       * STRING when nothing is — that emptiness is what lets the existing
       * omit-empty-optional / required logic in `submit` apply unchanged.
       * `validate` therefore sees the raw JSON string and parses it itself.
       * Dispatches as a real array (the submit loop `JSON.parse`s it).
       *
       * HOST CONTRACT: `groups` must be fully known when the modal MOUNTS —
       * `values` is a lazy `useState` initialised once, so a host with
       * fetched options must gate rendering the modal on the fetch.
       */
      readonly kind: "multiselect";
      readonly name: string;
      readonly label: string;
      readonly required?: boolean;
      /** JSON-encoded string[] (or "" for nothing pre-selected). */
      readonly defaultValue?: string;
      readonly groups: ReadonlyArray<MultiSelectGroup>;
    }
  | {
      readonly kind: "checkbox";
      readonly name: string;
      readonly label: string;
      readonly defaultValue?: boolean;
    };

/**
 * The modal half, without a trigger. Split out because a trigger that lives
 * inside a `<PopMenu>` CANNOT own the modal state: the menu renders
 * `{open ? children(close) : null}`, so closing it unmounts the whole subtree
 * — including the button that just set `open: true` on itself, portal and all.
 * A host in that position renders `<ActionModal>` itself, driven by its own
 * state, exactly as `MemberDetail`'s Consultation / Enrichment / Transfer /
 * Merge modals already are.
 */
export interface ActionModalProps {
  /** Canonical action wire name (e.g. `refund_payment`). */
  readonly action: string;
  readonly modalTitle: string;
  readonly description?: ReactNode;
  /** Locked context merged into the dispatch input (ids that must not be typed). */
  readonly prefill?: Record<string, unknown>;
  /** Editable fields. Omitted / empty → a confirm-only modal. */
  readonly fields?: ReadonlyArray<ActionField>;
  /**
   * Cross-field check run after the per-field required/number/date checks and
   * BEFORE dispatch. Return a sentence to block submission, `null` to allow.
   *
   * Exists because several actions express conditional requirements as JSON
   * Schema `allOf`/`if`/`then` (e.g. `record_inbox_event`: `stage:'closed'`
   * REQUIRES `closed_reason`). The per-field `required` flag cannot express
   * "required only when another field holds a particular value", so without
   * this the operator meets a raw Ajv `input_invalid` instead of a sentence.
   */
  readonly validate?: (
    values: Readonly<Record<string, string | boolean>>,
  ) => string | null;
  /**
   * Reshape the assembled input right before dispatch — after the per-field
   * coercions and `validate`, so it only ever sees an input that would
   * otherwise have been dispatched verbatim. Exists for actions whose wire
   * shape a flat field list cannot express — the first is
   * `create_staff_decision.options`, an ARRAY of `{label}` objects built
   * from `option_1..option_4` text fields. Pure reshaping only: dispatch
   * sends exactly what this returns.
   */
  readonly transformInput?: (
    input: Readonly<Record<string, unknown>>,
  ) => Record<string, unknown>;
  readonly submitLabel?: string;
  /** Fires after a successful dispatch — the host refetches its data. */
  readonly onDone?: (response: unknown) => void;
  /**
   * Render custom success content inside the modal (e.g. a coupon code, a
   * download link). When provided, the modal STAYS OPEN after success so staff
   * can read/copy it; otherwise the modal closes on success.
   */
  readonly renderSuccess?: (response: unknown) => ReactNode;
  /**
   * Render extra content under a refused dispatch's message (duplicate-member-
   * id, 2026-10-30: Add member's link to the member a duplicate collided with,
   * from `error.refs`). The message and code still render above it; return
   * `null` to add nothing.
   */
  readonly renderError?: (error: DispatchErrorState) => ReactNode;
  /** Dismiss — Cancel/Close, backdrop click, or a success that self-closes. */
  readonly onClose: () => void;
}

export interface ActionModalButtonProps
  extends Omit<ActionModalProps, "onClose"> {
  readonly buttonLabel: ReactNode;
  readonly buttonClassName?: string;
  readonly buttonTitle?: string;
  readonly disabled?: boolean;
  /**
   * Fires when the trigger opens the modal. NOTE: a host that closes a
   * `<PopMenu>` from here would unmount this component mid-open — such hosts
   * must render `<ActionModal>` directly instead (see its doc comment).
   */
  readonly onOpen?: () => void;
}

type FieldValue = string | boolean;

/**
 * field-errors (2026-11-01) — the rows a refusal's field may be named against:
 * each staff-entered field, by its label. A £ field this modal turns into pence
 * is left out: the rule's limit is in pence and would read wrong beside a
 * pounds box, so its refusal keeps the message.
 */
function refusalRows(fields: ReadonlyArray<ActionField>): Record<string, string> {
  return Object.fromEntries(
    fields
      .filter((f) => !(f.kind === "number" && f.minorUnits === true))
      .map((f) => [f.name, f.label]),
  );
}

function initialValues(
  fields: ReadonlyArray<ActionField>,
): Record<string, FieldValue> {
  const out: Record<string, FieldValue> = {};
  for (const f of fields) {
    if (f.kind === "checkbox") out[f.name] = f.defaultValue ?? false;
    else out[f.name] = f.defaultValue ?? "";
  }
  return out;
}

export function ActionModal({
  action,
  modalTitle,
  description,
  prefill,
  fields = [],
  submitLabel,
  onDone,
  renderSuccess,
  renderError,
  validate,
  transformInput,
  onClose,
}: ActionModalProps): JSX.Element {
  const [values, setValues] = useState<Record<string, FieldValue>>(() =>
    initialValues(fields),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<unknown | null>(null);
  const dispatch = useDispatch<Record<string, unknown>, unknown>(action);

  const close = (): void => {
    setValues(initialValues(fields));
    setLocalError(null);
    setSuccess(null);
    dispatch.reset();
    onClose();
  };
  // Press AND release on the backdrop — a scrollbar press on a tall modal's
  // overlay, or a text-selection drag-out, must not close it (shared hook).
  const backdrop = useBackdropDismiss(
    dispatch.loading ? (): void => undefined : close,
  );

  const setField = (name: string, v: FieldValue): void => {
    setValues((prev) => ({ ...prev, [name]: v }));
    setLocalError(null);
  };

  const submit = async (): Promise<void> => {
    const input: Record<string, unknown> = { ...(prefill ?? {}) };
    /**
     * Land a value at `input[name]`, or at `input[group][key]` when the field
     * name is dotted. ONE level only — the depth every action that takes a
     * sub-object (`update_member.patch`, `staff_create_member.metadata`)
     * actually declares, so a deeper path would fail schema validation at the
     * chokepoint rather than doing anything useful.
     */
    const put = (name: string, value: unknown): void => {
      const dot = name.indexOf(".");
      if (dot === -1) {
        input[name] = value;
        return;
      }
      const group = name.slice(0, dot);
      const key = name.slice(dot + 1);
      const bucket = (input[group] ?? {}) as Record<string, unknown>;
      bucket[key] = value;
      input[group] = bucket;
    };
    for (const f of fields) {
      const raw = values[f.name];
      if (f.kind === "checkbox") {
        put(f.name, raw === true);
        continue;
      }
      const str = typeof raw === "string" ? raw.trim() : "";
      if (str === "") {
        if (f.required === true) {
          setLocalError(`"${f.label}" is required.`);
          return;
        }
        continue; // omit empty optional fields
      }
      if (f.kind === "number") {
        const n = Number(str);
        if (!Number.isFinite(n)) {
          setLocalError(`"${f.label}" must be a number.`);
          return;
        }
        put(f.name, f.minorUnits === true ? Math.round(n * 100) : n);
      } else if (f.kind === "datetime") {
        // `datetime-local` is a bare wall-clock; interpret it in the STUDIO's
        // timezone, never the browser's (an operator abroad typing "14:00"
        // must record 14:00 at the studio, not 14:00 wherever they are).
        const iso = zonedDatetimeInputToIso(str);
        if (!Number.isFinite(Date.parse(iso))) {
          setLocalError(`"${f.label}" is not a valid date/time.`);
          return;
        }
        put(f.name, iso);
      } else if (f.kind === "select" && f.numeric === true) {
        put(f.name, Number(str));
      } else if (f.kind === "multiselect") {
        // The field component guarantees the value is either "" (already
        // dropped above) or a JSON-encoded string array.
        put(f.name, JSON.parse(str));
      } else {
        put(f.name, str);
      }
    }
    // Cross-field rules last: every single-field refusal has already fired, so
    // this only sees drafts that are individually well-formed.
    if (validate !== undefined) {
      const message = validate(values);
      if (message !== null) {
        setLocalError(message);
        return;
      }
    }
    const r = await dispatch.run(
      transformInput === undefined ? input : transformInput(input),
    );
    if (r.ok) {
      onDone?.(r.response);
      if (renderSuccess !== undefined) {
        setSuccess(r.response);
      } else {
        close();
      }
    }
  };

  return createPortal(
        <div className="modal-backdrop" role="presentation" {...backdrop}>
          <div className="modal-card" role="dialog" aria-modal="true">
            <h3 className="modal-title">{modalTitle}</h3>
            {description !== undefined ? (
              <p className="modal-subtitle">{description}</p>
            ) : null}

            {success !== null && renderSuccess !== undefined ? (
              <div className="action-modal-success">
                {renderSuccess(success)}
              </div>
            ) : (
              <>
                {fields.map((f) => (
                  <FieldRow
                    key={f.name}
                    field={f}
                    value={values[f.name] ?? (f.kind === "checkbox" ? false : "")}
                    onChange={(v) => setField(f.name, v)}
                  />
                ))}
                {localError !== null ? (
                  <p className="action-modal-error">{localError}</p>
                ) : null}
                {dispatch.error !== null ? (
                  <>
                    <p className="action-modal-error">
                      {failureText(dispatch.error, refusalRows(fields))} ({dispatch.error.code})
                    </p>
                    {renderError !== undefined ? renderError(dispatch.error) : null}
                  </>
                ) : null}
              </>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="row-edit-modal-cancel"
                onClick={close}
                disabled={dispatch.loading}
              >
                {success !== null ? "Close" : "Cancel"}
              </button>
              {success === null ? (
                <button
                  type="button"
                  className="row-edit-modal-save"
                  onClick={() => void submit()}
                  disabled={dispatch.loading}
                >
                  {dispatch.loading
                    ? "Working…"
                    : submitLabel ?? "Confirm"}
                </button>
              ) : null}
            </div>
          </div>
        </div>,
    document.body,
  );
}

/**
 * Trigger + modal. The common case: a button that lives directly on a page or
 * in a row, owning its own open state. A button inside a `<PopMenu>` must NOT
 * use this — see {@link ActionModalProps}.
 */
export function ActionModalButton({
  buttonLabel,
  buttonClassName,
  buttonTitle,
  disabled,
  onOpen,
  ...modalProps
}: ActionModalButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={buttonClassName ?? "action-dispatch-btn"}
        title={buttonTitle ?? ""}
        disabled={disabled === true}
        onClick={() => {
          setOpen(true);
          onOpen?.();
        }}
      >
        {buttonLabel}
      </button>
      {open ? (
        <ActionModal {...modalProps} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

function FieldRow({
  field,
  value,
  onChange,
}: {
  readonly field: ActionField;
  readonly value: FieldValue;
  readonly onChange: (v: FieldValue) => void;
}): JSX.Element {
  const captionId = useId();
  if (field.kind === "checkbox") {
    return (
      <label className="action-modal-checkbox">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />{" "}
        {field.label}
      </label>
    );
  }
  if (field.kind === "multiselect") {
    // A div, not the generic <label> wrapper below: the dropdown panel holds
    // its own checkboxes, and labels don't nest (the outer label's implicit
    // activation would fight the inner ones).
    return (
      <div className="modal-field">
        <span>
          {field.label}
          {field.required === true ? " *" : ""}
        </span>
        <MultiSelectField
          groups={field.groups}
          value={typeof value === "string" ? value : ""}
          onChange={(v) => onChange(v)}
        />
      </div>
    );
  }
  const strValue = typeof value === "string" ? value : "";
  if (field.kind === "select") {
    // A page-drawn dropdown, not a native <select> — see `SelectField` — and
    // a div, not the <label> wrapper below, for the multiselect's reason: a
    // click on an option inside a label re-opens the list.
    return (
      <div className="modal-field">
        <span id={captionId}>
          {field.label}
          {field.required === true ? " *" : ""}
        </span>
        <SelectField
          labelledBy={captionId}
          // A field may declare its own ""-valued option (e.g. the coupon
          // audience's "Anyone", whose empty value is what lets ActionModal
          // drop the field before dispatch); only a field without one gets
          // the "Choose…" placeholder row, so "" never names two options.
          options={
            field.options.some((o) => o.value === "")
              ? field.options
              : [{ value: "", label: "Choose…" }, ...field.options]
          }
          value={strValue}
          onChange={(v) => onChange(v)}
        />
      </div>
    );
  }
  return (
    <label className="modal-field">
      <span>
        {field.label}
        {field.required === true ? " *" : ""}
      </span>
      {field.kind === "textarea" ? (
        <textarea
          value={strValue}
          maxLength={field.maxLength}
          placeholder={field.placeholder ?? ""}
          rows={4}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type={
            field.kind === "number"
              ? "number"
              : field.kind === "datetime"
                ? "datetime-local"
                : "text"
          }
          value={strValue}
          {...(field.kind === "number"
            ? {
                ...(field.min !== undefined ? { min: field.min } : {}),
                ...(field.max !== undefined ? { max: field.max } : {}),
                ...(field.step !== undefined ? { step: field.step } : {}),
              }
            : {})}
          {...(field.kind === "text" && field.maxLength !== undefined
            ? { maxLength: field.maxLength }
            : {})}
          placeholder={
            field.kind === "number" || field.kind === "text"
              ? field.placeholder ?? ""
              : ""
          }
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}
